import { useMutation, useQueryClient } from "@tanstack/react-query";
import { $api } from "../generated/client";
import type { ShopDto, ShopReasonsDto } from "../generated/schema";
import { invalidateShopQueryKey } from "./use-invalidate-shop";

export default function useUpdateShopReasons(shopId: string) {
  const queryClient = useQueryClient();
  const mutation = $api.useMutation("patch", "/shops/{shopId}");

  return useMutation({
    onMutate: async (newReasons: ShopReasonsDto[]) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: invalidateShopQueryKey(shopId),
      });

      // Snapshot the previous value
      const previousShop = queryClient.getQueryData<ShopDto>(
        invalidateShopQueryKey(shopId),
      );

      // Optimistically update to the new value
      queryClient.setQueryData<ShopDto>(
        invalidateShopQueryKey(shopId),
        (old) => {
          if (!old) return old;
          return { ...old, reasons: newReasons };
        },
      );

      // Return a context object with the snapshotted value
      return { previousShop };
    },
    onError: (err, newReasons, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousShop) {
        queryClient.setQueryData(
          invalidateShopQueryKey(shopId),
          context.previousShop,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're in sync with the server
      queryClient.invalidateQueries({
        queryKey: invalidateShopQueryKey(shopId),
      });
    },
    mutationFn: async (reasons: ShopReasonsDto[]) => {
      return mutation.mutateAsync({
        params: { path: { shopId } },
        body: { reasons },
      });
    },
  });
}
